import { Component, EventEmitter, inject, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { BillItem } from 'src/app/models/bill-item.model';
import { Bill } from 'src/app/models/bill.model';
import { SettlementsService } from 'src/app/services/settlements.service';
import { AuthService } from 'src/app/shared/services/auth.service';
import { CatalogsService } from 'src/app/shared/services/catalogs.service';
import { IconsService } from 'src/app/shared/services/icons.service';
import { ToastrService } from 'ngx-toastr';
import { BillSaveData } from 'src/app/models/bill-save-data.interface';
import { BillKeysUpdatePipe } from 'src/app/pipes/bill-keys-update.pipe';
import { hasAppliedBalanceCredit, isBalanceCreditItem } from 'src/app/shared/utils/balance-credit.utils';
import { isBillItemCancelled } from 'src/app/shared/utils/reconnection-fee.utils';

@Component({
  selector: 'app-receipt-breakdown',
  template: `
    <h5>Detalles</h5>

    <div *ngIf="isLoading" class="text-center my-3">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Cargando...</span>
      </div>
      <p class="mt-2">Cargando detalles...</p>
    </div>
    <div *ngIf="!isLoading">
      <div class="d-none d-sm-block" *ngIf="interests">
        <table class="table table-sm mt-2 border-0" style="font-size: 0.8em;">
          <tbody class="text-dark">
            <tr class="border-bottom-secondary">
              <th>Periodo</th>
              <td class="text-end" *ngIf="bfc['periodo_inicio'].value">
                <editable-detail [editAllowed]="bill?.state === 0 && authService.hasPermission('can_create_bills')" (saveKey)="saveBillData($event)" [editValue]="bfc['periodo_inicio'].value" [inputType]="'date'" [keyForSaving]="'periodo_inicio'" />
              </td>
              <td class="text-end" *ngIf="bfc['periodo_fin']">
                <span *ngIf="!isEditingPeriodoFin && bill?.state === 0 && authService.hasPermission('can_create_bills')" 
                      class="d-inline-flex align-items-center"
                      style="cursor: pointer;"
                      (click)="startEditPeriodoFin()"
                      title="Click para editar">
                  <fa-icon [icon]="icons.faEdit" class="text-secondary me-2"></fa-icon>
                  {{bfc['periodo_fin'].value | date: 'dd/MM/yy' }}
                </span>
                <span *ngIf="!isEditingPeriodoFin && (bill?.state !== 0 || !authService.hasPermission('can_create_bills'))">
                  {{bfc['periodo_fin'].value | date: 'dd/MM/yy' }}
                </span>
                <div *ngIf="isEditingPeriodoFin" class="d-flex align-items-center">
                  <input type="date" 
                         class="form-control form-control-sm me-2" 
                         style="width: 140px; font-size: 0.8em;"
                         [(ngModel)]="editedPeriodoFin"
                         (keyup.enter)="savePeriodoFin()"
                         (keyup.escape)="cancelEditPeriodoFin()"
                         #dateInput>
                  <button class="btn btn-sm btn-success me-1" 
                          (click)="savePeriodoFin()"
                          title="Guardar">
                    <fa-icon [icon]="icons.faCircleCheck" size="xs"></fa-icon>
                  </button>
                  <button class="btn btn-sm btn-secondary" 
                          (click)="cancelEditPeriodoFin()"
                          title="Cancelar">
                    <fa-icon [icon]="icons.faTimes" size="xs"></fa-icon>
                  </button>
                </div>
              </td>
            </tr>
            <tr class="border-bottom-secondary"> 
              <th colspan="2">Fecha de vencimiento</th>
              <td colspan="2" class="text-end" *ngIf="bfc['due_date']">{{bfc['due_date'].value | date: 'dd/MM/yy' }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      
      <!-- Consumption Items -->
      <div class="d-none d-sm-block" *ngIf="consumptionItems!.length > 0">
        <table class="table table-sm mt-2 border-0" style="font-size: 0.8em;">
          <tbody class="text-dark">
            <tr>
              <td colspan="8" scope="col" class="text-start"><b>Concepto</b></td>
              <td colspan="1" scope="col" class="text-center">Cant.</td>
              <td colspan="1" scope="col" class="text-center">P.U.</td>
              <td colspan="1" scope="col" class="text-center">Dto.</td>
              <td colspan="1" scope="col" class="text-end">Total</td>
            </tr>
            <tr *ngFor="let item of consumptionItems">
              <td colspan="8" scope="col" class="ps-0"><b>Consumo de Gas</b></td>
              <td colspan="1" scope="col" class="text-center text-secondary">
                <editable-detail
                [editAllowed]="bill?.state === 0 && authService.hasPermission('can_create_bills')"
                (saveKey)="saveBillData($event, consumptionItems ?? [], item.id)"
                [editValue]="item.quantity" [inputType]="'number'" [keyForSaving]="'quantity'" />
              </td>
              <td colspan="1" scope="col" class="text-center text-secondary">
                <editable-detail
                [editAllowed]="bill?.state === 0 && authService.hasPermission('can_create_bills')"
                (saveKey)="saveBillData($event, consumptionItems ?? [], item.id)"
                [editValue]="item.price" [isCurrency]="true" [inputType]="'number'" [keyForSaving]="'price'" />
              </td>
              <td colspan="1" scope="col" class="text-center text-secondary">
                <editable-detail
                [editAllowed]="bill?.state === 0 && authService.hasPermission('can_create_bills')"
                (saveKey)="saveBillData($event, consumptionItems ?? [], item.id)"
                [editValue]="item.discount_amount" [isCurrency]="true" [inputType]="'number'" [keyForSaving]="'discount_amount'" />
              </td>
              <td colspan="1" scope="col" class="text-end">{{item.total  | currency: 'USD'}}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Other Services -->
      <div class="d-none d-sm-block" *ngIf="otherItems!.length > 0">
        <table class="table table-sm mt-2 border-0" style="font-size: 0.8em;">
          <tbody class="text-dark">
            <tr>
              <td colspan="8" scope="col" class="text-start ps-0"><b>Servicios</b></td>
              <td colspan="1" scope="col" class="text-center">Cant.</td>
              <td colspan="1" scope="col" class="text-center">P.U.</td>
              <td colspan="1" scope="col" class="text-center">Dto.</td>
              <td colspan="1" scope="col" class="text-end">Total</td>
            </tr>
            <tr *ngFor="let item of otherItems">
              <td colspan="8" scope="col" class="ps-0" [ngClass]="{'cancelled-item': isCancelled(item)}">
                <div class="d-flex align-items-start justify-content-between">
                  <div><b>{{getItemType(item.item_type)}}</b></div>
                  <div class="" *ngIf="bill?.state === 0 && !isCancelled(item)">
                    <button type="button" class="btn btn-link text-danger p-0" 
                      [ngStyle]="{'font-size': '0.8em', 'padding': '0px 5px'}"
                      *ngIf="item.item_type === 'late_fee' || item.item_type === 'service' || item.item_type === 'product' || item.item_type === 'debt' || item.item_type === 'reconnection_fee'"
                      (click)="destroyBillItem(item)"
                    >
                      <fa-icon [icon]="icons.faBan"></fa-icon>
                    </button> 
                  </div>
                </div>
              </td>
              <td colspan="1" scope="col" [ngClass]="{'cancelled-item': isCancelled(item)}" class="text-center text-secondary">{{item.quantity | number: '1.2-2'}}</td>
              <td colspan="1" scope="col" [ngClass]="{'cancelled-item': isCancelled(item)}" class="text-center text-secondary">
                <editable-detail
                [editAllowed]="!isBalanceCredit(item) && bill?.state === 0 && authService.hasPermission('can_create_bills')"
                (saveKey)="saveBillData($event, otherItems ?? [], item.id)"
                [editValue]="item.price" [isCurrency]="true" [inputType]="'number'" [keyForSaving]="'price'" />
              </td>
              <td colspan="1" scope="col" [ngClass]="{'cancelled-item': isCancelled(item)}" class="text-center text-secondary">{{item.discount_amount | currency: 'USD'}}</td>
              <td colspan="1" scope="col" [ngClass]="{'cancelled-item': isCancelled(item)}" class="text-end">{{item.total | currency: 'USD'}}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <!-- Totals -->
      <table class="table table-sm mt-4 border-0" style="font-size: 0.8em;">
        <thead>
          <tr>
            <td class="text-secondary">Subtotal</td>
            <td colspan="2" scope="col" class="text-end text-secondary">{{subtotal | currency: 'USD'}}</td>
          </tr>
          <tr>
            <td class="text-secondary">IVA</td>
            <td colspan="2" scope="col" class="text-end text-secondary">{{taxes | currency: 'USD'}}</td>
          </tr>
          <tr>
            <th>Total</th>
            <th colspan="2" scope="col" class="text-end">{{total | currency: 'USD'}}</th>              
          </tr>
          <tr>
            <td colspan="3" class="text-secondary">
              <p class="text-center d-none d-sm-block text-sm mb-0" *ngIf="total"><strong>{{total.toFixed(2) | currencyToText}}</strong></p>
            </td>
          </tr>
        </thead>
      </table>
    </div>
  `,
  styleUrls: []
})
export class ReceiptBreakdownComponent implements OnChanges {
  @Input() bfc: any;
  @Input() bill: Bill | null = null;
  @Input() bill_items: BillItem[] | null = null;
  @Input() prev_bill: Bill | null = null;
  @Input() interests: any;
  @Output() onDestroyItem = new EventEmitter<BillItem>();
  @Output() billUpdated = new EventEmitter<Bill>();
  @Output() loadBillByUpdate = new EventEmitter<boolean>();
  billKeyUpdatePipe = inject(BillKeysUpdatePipe);
  consumptionItems: any[] | null = null;
  otherItems: any[] | null = null;
  
  subtotal: number = 0;
  taxes: number = 0;
  total: number = 0;
  totalConsumption: number = 0;
  isLoading: boolean = true;

  // Periodo fin editing properties
  isEditingPeriodoFin: boolean = false;
  editedPeriodoFin: string = '';

  constructor(
    public catalogService: CatalogsService,
    public icons: IconsService,
    public authService: AuthService,
    private settlementsService: SettlementsService,
    private toastr: ToastrService
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    if(changes['bill']) {
      this.calculateTotals();
    }
    if (changes['bill_items']) {
      this.groupItems();
      this.isLoading = false;
      this.calculateTotals();
    }
    if (changes['prev_bill']) {
      this.calculateTotals();
    }
  }

  destroyBillItem(item: BillItem) {
    const itemType = this.getItemType(item?.item_type ?? '');
    const confirmMessage = `¿Está seguro que desea eliminar "${itemType}" de la factura?`;
  
    if (confirm(confirmMessage)) {
      this.onDestroyItem.emit(item);
    }
  }

  isCancelled(item: BillItem): boolean {
    return isBillItemCancelled(item);
  }

  isBalanceCredit(item: BillItem): boolean {
    return isBalanceCreditItem(item);
  }

  getItemType(itemType: string) {
    const item_type = this.catalogService.conceptTypes.find((type: any) => type.name === itemType);
    return item_type ? item_type.description : 'Desconocido';
  }

  private groupItems() {
    this.consumptionItems = (this.bill_items ?? []).filter(item => item.item_type === 'consumption');
    this.otherItems = (this.bill_items ?? []).filter(
      item => item.item_type !== 'consumption' && (!isBalanceCreditItem(item) || hasAppliedBalanceCredit(item))
    );
  }

  private calculateTotals() {
    if (this.bill) {
      this.subtotal = Number(this.bill!.sub_total);
      this.taxes = Number(this.bill!.taxes);
      this.total = Number(this.bill!.total);
    }
  }

  startEditPeriodoFin() {
    if (this.bill?.state !== 0 || !this.authService.hasPermission('can_create_bills')) {
      return; // Only allow editing if bill state is 0 and user has permission
    }
    
    this.isEditingPeriodoFin = true;
    // Convert the current periodo_fin to the input format (YYYY-MM-DD)
    const currentDate = this.bfc['periodo_fin'].value;
    if (currentDate) {
      // Assuming the date is in ISO format or can be parsed by Date constructor
      const date = new Date(currentDate);
      this.editedPeriodoFin = date.toISOString().split('T')[0];
    }
    
    // Focus the input after a short delay to ensure it's rendered
    setTimeout(() => {
      const input = document.querySelector('input[type="date"]') as HTMLInputElement;
      if (input) {
        input.focus();
      }
    }, 100);
  }

  cancelEditPeriodoFin() {
    this.isEditingPeriodoFin = false;
    this.editedPeriodoFin = '';
  }

  saveBillData(data: BillSaveData, array?: any[], idItem?: string) {
    const { type, key, value } = data;
    const updateData = {
      [key]: ['quantity', 'price', 'discount_amount'].includes(key) ? Number(value) : value
    };
    if (array && idItem) {
      this.settlementsService.updateBillItem(idItem, updateData).subscribe({
        next: (_res) => {
          const item = array.find(itemArray => itemArray.id === idItem);
          if (item) {
            item[key] = value;
            item.total = _res.data.attributes.total;
          }
          this.toastr.success(`${this.billKeyUpdatePipe.transform(key)} actualizado correctamente`, 'Éxito');
          this.loadBillByUpdate.emit(true);
        }
      })
    } else {
      this.settlementsService.updateBill(this.bill?.id, updateData).subscribe({
        next: (_res) => {
          // Update the form control value
          this.bfc[key].setValue(value);
          // Update the bill object
          if (this.bill) {
            (this.bill as any)[key] = type === 'date' && value != null ? new Date(value) : value;
          }
          // Emit the updated bill
          this.billUpdated.emit(this.bill!);
          this.toastr.success(`${this.billKeyUpdatePipe.transform(key)} actualizado correctamente`, 'Éxito');
          this.loadBillByUpdate.emit(true);
        }
      });
    }
  }

  savePeriodoFin() {
    if (!this.editedPeriodoFin || !this.bill) {
      this.toastr.error('Por favor seleccione una fecha válida', 'Error');
      return;
    }

    const updateData = {
      periodo_fin: this.editedPeriodoFin
    };

    this.settlementsService.updateBill(this.bill.id, updateData).subscribe({
      next: (response: any) => {
        // Update the form control value
        this.bfc['periodo_fin'].setValue(this.editedPeriodoFin);
        
        // Update the bill object
        if (this.bill) {
          this.bill.periodo_fin = new Date(this.editedPeriodoFin);
        }
        
        // Emit the updated bill
        this.billUpdated.emit(this.bill!);
        
        this.toastr.success('Periodo final actualizado correctamente', 'Éxito');
        this.isEditingPeriodoFin = false;
        this.editedPeriodoFin = '';
      },
      error: (error: any) => {
        console.error('Error updating periodo_fin:', error);
        this.toastr.error('Error al actualizar el periodo final', 'Error');
      }
    });
  }
}
